import type { ModuleDef } from './types.js';

/**
 * Sensitive information: FIELD-level control over employee data, in the same
 * grid as everything else. A module here does not name a screen; it names a
 * group of employee fields (see `sensitiveFields.ts` for which) that the API
 * masks in a response when the caller lacks `view`.
 *
 * `edit` only makes sense with `view` — a role that cannot read a value cannot
 * be trusted to overwrite it blind — so every `edit` description says so, the
 * catalog test holds that, and the Roles screen ticks/unticks the pair together.
 *
 * NOBODY holds these by default — not Administrator, not HR (`defaultRoles` is
 * empty on all four, and the Administrator "holds everything" rule excludes this
 * group). Access is GRANTED, per person (People tab → Allow) or per role. Anyone
 * without it sees the values MASKED to their last four characters.
 *
 * A person's OWN record is never masked and their self-service edits are not
 * gated here: these boxes are about reading and changing OTHER people's data.
 */
export const SENSITIVE_MODULES: readonly ModuleDef[] = [
  {
    key: 'sensitive.bank',
    label: 'Bank details',
    group: 'sensitive',
    permissions: [
      { action: 'view', defaultRoles: [], description: 'Read the bank name, account number and IBAN of other employees' },
      { action: 'edit', defaultRoles: [], description: "Change other employees' bank details (needs View)" },
    ],
  },
  {
    key: 'sensitive.identity',
    label: 'ID & passport numbers',
    group: 'sensitive',
    permissions: [
      { action: 'view', defaultRoles: [], description: 'Read the passport, Emirates ID, visa and labour card numbers of other employees' },
      { action: 'edit', defaultRoles: [], description: "Change other employees' passport, Emirates ID, visa and labour card numbers (needs View)" },
    ],
  },
  {
    key: 'sensitive.salary',
    label: 'Salary & pay figures',
    group: 'sensitive',
    permissions: [
      { action: 'view', defaultRoles: [], description: "Read other employees' salary, allowances and every figure derived from them: payslips, payroll, gratuity, loans, labour cost and salary certificates" },
      { action: 'edit', defaultRoles: [], description: "Change other employees' salary and allowances (needs View)" },
    ],
  },
];
